import {
  Code,
  Divider,
  Heading,
  Link,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";

import {useFont} from "..//hooks/FontProvider";

type MarkdownProps = {
  content: string;
  compact?: boolean;
};

const Markdown = ({ content, compact }: MarkdownProps) => {
  const font = useFont().font;
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        blockquote: ({ children }) => <Code>{children}</Code>,
        pre: ({ children }) => <Code>{children}</Code>,
        code: ({ children, inline, className }) =>
          inline ? (
            <Code className={className}>{children}</Code>
          ) : (
            <SyntaxHighlighter
              children={String(children).replace(/\n$/, "")}
              style={atomDark}
              showLineNumbers={true}
            />
          ),
        a: ({ children, href }) => (
          <Link color="teal.500" href={href} target="_blank">
            {children}
          </Link>
        ),
        h1: ({ children }) => (
          <Heading as="h1" marginBottom={compact ? 0 : 6} size='2xl'
                   fontWeight={font.weight} fontSize={font.h1}>
            {children}
          </Heading>
        ),
        h2: ({ children }) => (
          <Heading as="h2" marginBottom={compact ? 0 : 6} size='xl'
                   fontWeight={font.weight} fontSize={font.h2}>
            {children}
          </Heading>
        ),
        h3: ({ children }) => (
          <Heading as="h3" marginBottom={compact ? 0 : 6} size='lg'
                   fontWeight={font.weight} fontSize={font.h3}>
            {children}
          </Heading>
        ),
        h4: ({ children }) => (
          <Heading as="h4" marginBottom={compact ? 0 : 6} size='md'
                   fontWeight={font.weight} fontSize={font.h4}>
            {children}
          </Heading>
        ),
        h5: ({ children }) => (
          <Heading as="h5" marginBottom={compact ? 0 : 6} size='sm'
                   fontWeight={font.weight} fontSize={font.h5}>
            {children}
          </Heading>
        ),
        h6: ({ children }) => (
          <Heading as="h6" marginBottom={compact ? 0 : 6} size='xs'
                   fontWeight={font.weight} fontSize={font.h6}>
            {children}
          </Heading>
        ),

        hr: () => <Divider marginBottom={compact ? 0 : 6} />,
        li: ({ children }) => <ListItem userSelect={"none"}>{children}</ListItem>,
        ol: ({ children }) => (
          <OrderedList userSelect={"none"} marginBottom={compact ? 0 : 6}>{children}</OrderedList>
        ),
        p: ({ children }) => (
          <Text userSelect={"none"} marginBottom={compact ? 0 : 6}
                fontWeight={font.weight} fontSize={font.p}>
            {children}
          </Text>
        ),
        ul: ({ children }) => (
          <UnorderedList userSelect={"none"} marginBottom={compact ? 0 : 6}>
            {children}
          </UnorderedList>
        ),


      }}
      children={content}
    />
  );
};

export default Markdown;
